import React, { memo, useCallback, useMemo } from "react"
import { useActiveSessions } from "@/common/queries/use-active-sessions"
import { useFetchLocation } from "@/common/queries/use-fetch-location"
import { ChevronRight, X } from "lucide-react"
import { useShallow } from "zustand/react/shallow"

import type { Session } from "@be-miniapp/api"
import { useTranslation } from "@be-miniapp/i18n"
import { useClientSessionSetting } from "@be-miniapp/shared/stores/client-session-setting"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { SearchIcon } from "@be-miniapp/ui/components/ui/icons/search"
import { SpinnerScreen } from "@be-miniapp/ui/components/ui/spinner"
import { Typography } from "@be-miniapp/ui/components/ui/typography"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"
import { cn } from "@be-miniapp/ui/lib/utils"

import { useRideDataStore } from "@/store/ride-data"
import { Sheet } from "@/components/sheet"

import { RecommendedAddressHomeScreen } from "./recommended-address"

const ActiveSessionsSheet = memo(
  ({
    visible,
    sessions,
    onClose,
    onSessionClick,
  }: {
    visible: boolean
    sessions: Session[]
    onClose: () => void
    onSessionClick: (session: Session) => void
  }) => (
    <Sheet visible={visible} onClose={onClose} autoHeight mask maskClosable>
      <div className="flex w-full flex-col divide-y divide-gray-100 overflow-hidden rounded-t-lg bg-white">
        <div className="flex items-center justify-between px-4 py-3 font-semibold">
          <span className="text-sm">Hoạt động đang có</span>
          <X size={18} onClick={onClose} />
        </div>
        <div className="flex flex-1 flex-col divide-y divide-gray-200/50 overflow-y-auto p-1">
          {sessions.map((session) => {
            return (
              <div
                key={session.type + session.trip_id}
                className="flex items-center gap-3 rounded-md bg-white p-3"
                onClick={() => {
                  onSessionClick(session)
                }}
              >
                <div className="shrink-0">
                  {session.image && (
                    <img src={session.image} className="size-8" />
                  )}
                </div>

                <div className="flex flex-col overflow-hidden">
                  <span className="truncate font-bold text-gray-800 text-sm">
                    {session.title_text}
                  </span>
                  <span
                    style={{ color: session.status_color }}
                    className="truncate text-gray-500 text-sm"
                  >
                    {session.status_text}
                  </span>
                </div>

                <div className="ml-auto">
                  <ChevronRight color={session.status_color} size={16} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Sheet>
  )
)

ActiveSessionsSheet.displayName = "ActiveSessionsSheet"

interface ActiveSessionsProps {
  showDropoffInput?: boolean
}

const ActiveSessions = ({ showDropoffInput = true }: ActiveSessionsProps) => {
  const { t } = useTranslation()

  const {
    state: currentState,
    engagement,
    setSessionId,
  } = useRideDataStore(
    useShallow((state) => ({
      state: state.state,
      engagement: state.engagement,
      setSessionId: state.setSessionId,
    }))
  )
  const { data: activeSessions } = useActiveSessions({
    data: {
      engagementId: `${engagement?.engagement_id}`,
      currentState: currentState ?? "",
    },
  })

  const changeActivityTab = useClientSessionSetting(
    (state) => state.changeActivityTab
  )

  const navigate = useNavigate()

  const [isFetchingSessionData] = React.useState(false)

  const handleSessionClick = useCallback(
    async (session: Session) => {
      if (session.type === 1) {
        setSessionId(session.trip_id)

        navigate(`/ride/intrip?session_id=${session?.trip_id}`)

        return
      }

      if (session.redirect_to === "FOOD_ACTIVITY") {
        navigate("/ride/home/history", {
          state: {
            resetScroll: true,
          },
        })
        changeActivityTab("food")
        return
      }

      if (session.type === 5) {
        navigate(`/food/order/${session.trip_id}`)
        return
      }

      navigate("/non-supported", {
        state: {
          isSession: true,
        },
      })
    },

    [navigate, changeActivityTab, setSessionId]
  )

  const [showList, setShowList] = React.useState(false)

  const { fetch: fetchLocationBeforeRide, isFetching } = useFetchLocation()

  const sessions = activeSessions?.sessions ?? []
  const totalSession = sessions.length

  const transportSession = sessions.find((session) => session.type === 1)
  const firstSession = sessions[0]

  const isMultipleTypeOfSession =
    (activeSessions?.active_transport_session_count ?? 0) >= 2 ||
    ((activeSessions?.active_transport_session_count ?? 0) > 0 &&
      activeSessions?.active_transport_session_count !==
        activeSessions?.active_session_count)

  const handleOpenDropoff = useCallback(async () => {
    await fetchLocationBeforeRide()
    navigate("/ride/dropoff_location", {
      state: {
        resetLocation: true,
      },
    })
  }, [fetchLocationBeforeRide, navigate])

  const bannerTitle = useMemo(
    () =>
      totalSession === 1 ? firstSession?.title_text : activeSessions?.title,
    [activeSessions?.title, firstSession?.title_text, totalSession]
  )

  const bannerStatus = useMemo(
    () =>
      !transportSession
        ? firstSession?.status_text
        : transportSession.status_text,
    [firstSession?.status_text, transportSession]
  )

  const bannerStatusColor =
    transportSession?.status_color ?? firstSession?.status_color

  if (!showDropoffInput && totalSession === 0) {
    return null
  }

  return (
    <div>
      {isFetching || isFetchingSessionData ? <SpinnerScreen /> : null}
      <div
        className={cn(
          "flex animate-fade-slide-up-sm flex-col overflow-hidden",
          showDropoffInput &&
            "rounded-md shadow-[rgba(0,0,0,0.18)_-4px_10px_25px_-6px]"
        )}
      >
        {showDropoffInput ? (
          <div
            className={cn(
              "flex flex-col border-gray-200/60 border-x border-t bg-white",
              totalSession === 0 ? "rounded-md border-b" : "rounded-t-md"
            )}
          >
            <Button
              variant="ghost"
              size="auto"
              onClick={handleOpenDropoff}
              className="flex items-center justify-start gap-2 p-3"
            >
              <SearchIcon
                name="destination"
                className="size-5"
                strokeWidth={1}
              />
              <Typography.Body className="text-gray-500 text-sm">
                {t("home.input_dropoff")}
              </Typography.Body>
            </Button>

            <RecommendedAddressHomeScreen />
          </div>
        ) : null}

        {totalSession > 0 && (
          <div
            onClick={() => {
              if (isMultipleTypeOfSession) {
                setShowList(true)
                return
              }
              const first = firstSession
              if (first) handleSessionClick(first)
            }}
            className={cn(
              "flex animate-fade-slide-down cursor-pointer items-center gap-3 bg-[#EAF4FF] px-2 py-3",
              showDropoffInput && "rounded-b-md"
            )}
          >
            <div className="shrink-0 pl-2">
              {firstSession?.image && (
                <img src={firstSession.image || ""} className="size-8" />
              )}
            </div>

            <div className="flex max-w-full flex-col overflow-hidden break-words">
              <span className="truncate font-bold text-gray-800 text-sm">
                {bannerTitle}
              </span>
              <span
                style={{ color: bannerStatusColor }}
                className="text-[12px] text-gray-500"
              >
                {bannerStatus}
              </span>
            </div>

            <div className="ml-auto">
              <ChevronRight size={15} color={bannerStatusColor} />
            </div>
          </div>
        )}
      </div>

      <ActiveSessionsSheet
        visible={showList}
        sessions={sessions}
        onClose={() => setShowList(false)}
        onSessionClick={handleSessionClick}
      />
    </div>
  )
}

export { ActiveSessions }
